import React, { useState, useEffect } from 'react';
import {
  Button, Col, Row, Input, Spin,
  Table, Form, notification,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import PlusIcon from 'mdi-react/PlusIcon';
import {
  EditOutlined,
} from '@ant-design/icons';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import FilterIcon from 'mdi-react/FilterIcon';
import './Branches.scss';
import {
  get, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { backendResponseCodes } from '../../../utils/responseMessages';
import { getUserPermissions } from '../../../utils/users';

interface Branch {
  id?: number,
  name: string,
  city: string,
  uf: string,
  state: string,
  address: string,
  complement?: string,
  cep: string,
  phone: string,
  email?: string,
  segment?: string,
  lat?: string,
  log?: string,
  displayRequest: boolean,
  status: string,
}

interface TablePagination {
  current: number | undefined,
  defaultCurrent: number | undefined,
  pageSize: number | undefined,
  showQuickJumper: boolean | {
    goButton?: React.ReactNode;
  },
  total: number | undefined,
}

const Branches = (): React.ReactElement => {
  const navigate = useNavigate();
  const userPermissions = getUserPermissions();
  const [formInstance] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<Branch>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });

  /**
   * @name executeFilter
   * @param args
   * @returns retorna a listagem de filiais na página filtrada e paginada
   */
  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'name',
    orderByDir: 'ASC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const values = formInstance.getFieldsValue();

    const argsQuery = {
      search: (values.search != null) ? values.search : undefined,
      orderBy: ('orderBy' in args) ? args.orderBy : 'name',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'ASC',
    };

    const requestReference: RequestWrapper = get({
      url: '/branch/findAll',

      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 16003): {
        // "Notification category request successfully", 16003
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        setTableDataSource(response?.body?.branchCoopercitrusDetailsOutputList);

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
      } break;

      case (responseError?.body?.code === -4001):
        break;

      case (responseError?.body?.code === -4003):
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar as filiais, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  useEffect(() => {
    setLoading(true);

    const executeOnMount = async (): Promise<void> => {
      await executeFilter();
    };

    if ((userPermissions || []).includes('Visualizar filiais solicitados')) {
      executeOnMount(); // carregamento da lista considerando filtragem e paginação
    } else {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, o usuário não possui permissão para consultar as filiais.',
      });
    }

    setLoading(false);

    return () => {
      setLoading(false);
    };
  }, []);

  /**
   * @name handleTableChange
   * @param pagination
   * @param _filters
   * @param sorter
   * @returns retorna o estado da tabela atualizado e a listagem em página
   */
  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'name',
      orderByDir: finalOrderDir || 'ASC',
      pageSize: pagination.pageSize,
    });
  };

  return (
    <div className="Branches">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >

        <Row
          className="Branches__ComponentRow"
          gutter={[24, 24]}
        >
          <Col
            className="Branches__ComponentColumn"
            xs={{ span: 24 }}
          >
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className="Branches__PageTitleRow"
            >
              <Col
                className="Branches__PageTitleColumn"
              >
                <Typography.Title
                  className="Branches__PageTitle"
                >
                  Filiais
                </Typography.Title>
              </Col>

              <Col
                className="Branches__ButtonColumn"
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 24 }}
                lg={{ span: 12 }}
                xl={{ span: 10 }}
              >
                <Row
                  gutter={24}
                  justify="end"
                  className="Branches__NestedButtonRow"
                >
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    lg={{ span: 12 }}
                    className="Branches__BackButtonColumn"
                  >
                    <Button
                      type="default"
                      href="/home"
                      className="Branches__BackButton"
                      onClick={
                        (eventt): void => {
                          eventt.preventDefault();
                          navigate('/home');
                        }
                      }
                      block
                    >
                      Voltar
                    </Button>
                  </Col>

                  {
                    (userPermissions || []).includes('Solicitar nova filial')
                      ? (
                        <Col
                          xs={{ span: 24 }}
                          sm={{ span: 12 }}
                          lg={{ span: 12 }}
                          className="Branches__NewEventButtonColumn"
                        >
                          <Button
                            className="Branches__ButtonIcon"
                            block
                            type="primary"
                            href="/system/branches/new"
                            onClick={(eventt): void => {
                              eventt.preventDefault();
                              navigate('/system/branches/new');
                            }}
                          >
                            <PlusIcon />
                            Cadastrar filial
                          </Button>
                        </Col>
                      )
                      : ''
                  }
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>

        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onFinish={
            async (): Promise<void> => {
              await executeFilter();
            }
          }
        >
          <Row
            gutter={24}
            className="filters-container"
          >
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
                name="search"
                rules={[]}
              >
                <Input
                  style={{ width: '100%' }}
                  placeholder="Nome / Cidade / Estado / Segmento"
                >
                </Input>
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-clean"
                  htmlType="reset"
                  onClick={
                    (): void => {
                      formInstance.resetFields();
                      executeFilter();
                    }
                  }
                >
                  <FilterRemoveIcon />
                  Limpar
                </Button>
              </Form.Item>
            </Col>

            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label=" "
              >
                <Button
                  className="btn-filter"
                  type="primary"
                  htmlType="submit"
                >
                  <FilterIcon />
                  Filtrar
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    className="Branches__Table"
                    columns={[
                      {
                        title: <div className="table-titles">ID</div>,
                        dataIndex: 'id',
                        key: 'id',
                        ellipsis: true,
                        sorter: true,
                      },
                      {
                        title: <div className="table-titles">Nome</div>,
                        key: 'name',
                        dataIndex: 'name',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                      },
                      {
                        title: <div className="table-titles">Cidade</div>,
                        key: 'city',
                        dataIndex: 'city',
                        ellipsis: true,
                        sorter: true,
                      },
                      {
                        title: <div className="table-titles">UF</div>,
                        dataIndex: 'uf',
                        key: 'uf',
                        ellipsis: true,
                        sorter: true,
                      },
                      {
                        title: <div className="table-titles">Segmento</div>,
                        dataIndex: 'segment',
                        key: 'segment',
                        ellipsis: true,
                        sorter: true,
                      },
                      {
                        title: <div className="table-titles">Status</div>,
                        dataIndex: 'status',
                        key: 'status',
                        ellipsis: true,
                        sorter: true,
                        render: (
                          _value: string,
                          record: Branch,
                        ): React.ReactNode => {
                          return (
                            `${record.status === 'DELETED' ? 'Inativo' : 'Ativo'}`
                          );
                        },
                      },
                      {
                        title: <div className="table-titles">Ações</div>,
                        dataIndex: 'id',
                        key: 'opcoes',
                        render: (
                          _value: string | number,
                          record: Branch,
                        ): React.ReactNode => {
                          return (
                            <>
                              {
                                (userPermissions || []).includes('Editar filial')
                                  ? (
                                    <Button
                                      type="link"
                                      title="Editar filial"
                                      icon={<EditOutlined />}
                                      href={`/system/branches/${record.id}/edit`}
                                      onClick={
                                        (eventt): void => {
                                          eventt.preventDefault();
                                          navigate(`/system/branches/${record.id}/edit`);
                                        }
                                      }
                                    />
                                  )
                                  : null
                              }
                            </>
                          );
                        },
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.id as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

Branches.propTypes = {};
Branches.defaultProps = {};

export default Branches;
